import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import ProgressBar from '../../components/ProgressBar';
import { Input, TextArea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { getImageUrl } from '../../utils/imageUtils';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  label { display: block; margin-bottom: 0.5rem; font-weight:500; }
`;

const HeaderRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin: 2rem 0 1rem;
`;

const BlockEditorWrapper = styled.div`
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1rem;
  background: #fafafa;
`;

const BlockRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const BlockTypeSelect = styled.select`
  padding: 0.4rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.85rem;
  min-width: 110px;
  background: white;
`;

const BlockInput = styled.textarea`
  flex: 1;
  padding: 0.4rem 0.6rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 38px;
  font-family: inherit;
`;

const BlockDeleteBtn = styled.button`
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
`;

const AddBlockBtn = styled.button`
  background: none;
  border: 1px dashed #aaa;
  border-radius: 4px;
  padding: 0.3rem 0.8rem;
  cursor: pointer;
  color: #555;
  font-size: 0.85rem;
  margin-right: 0.5rem;
  &:hover { background: #f0f0f0; }
`;

const parseDescription = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return [{ type: 'text', content: raw }];
};

const serializeDescription = (blocks) => JSON.stringify(blocks);

const DescriptionEditor = ({ blocks, onChange }) => {
  const addBlock = (type) => onChange([...blocks, { type, content: '' }]);
  const updateBlock = (i, field, val) => {
    const next = [...blocks];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const removeBlock = (i) => onChange(blocks.filter((_, idx) => idx !== i));

  return (
    <BlockEditorWrapper>
      {blocks.map((block, i) => (
        <BlockRow key={i}>
          <BlockTypeSelect value={block.type} onChange={e => updateBlock(i, 'type', e.target.value)}>
            <option value="heading">Heading</option>
            <option value="subheading">Subheading</option>
            <option value="text">Text</option>
          </BlockTypeSelect>
          <BlockInput
            value={block.content}
            onChange={e => updateBlock(i, 'content', e.target.value)}
            placeholder={block.type === 'heading' ? 'Bold & underlined heading...' : block.type === 'subheading' ? 'Bold subheading...' : 'Regular text...'}
          />
          <BlockDeleteBtn onClick={() => removeBlock(i)} title="Remove block">×</BlockDeleteBtn>
        </BlockRow>
      ))}
      <div style={{marginTop: '0.5rem'}}>
        <AddBlockBtn onClick={() => addBlock('heading')}>+ Heading</AddBlockBtn>
        <AddBlockBtn onClick={() => addBlock('subheading')}>+ Subheading</AddBlockBtn>
        <AddBlockBtn onClick={() => addBlock('text')}>+ Text</AddBlockBtn>
      </div>
    </BlockEditorWrapper>
  );
};

const WorkshopsAdmin = () => {
  const [header, setHeader] = useState({ title: '', description: '', image: '' });
  const [info, setInfo] = useState({ title: '', description: '', image: '' });
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState('list');
  const [currentPost, setCurrentPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [progress, setProgress] = useState({ header: 0, info: 0, post: 0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [h, i, p] = await Promise.all([
        api.get('/workshops/header'),
        api.get('/workshops/info'),
        api.get('/workshops/posts')
      ]);
      setHeader(h);
      setInfo(i);
      setPosts(p);
    } catch (err) { console.error(err); }
  };

  const saveHeader = async () => {
    setSaving(true);
    try {
      await api.put('/workshops/header', { title: header.title, description: header.description });
      setMsg('Header saved');
    } catch (err) { setMsg('Error saving header'); }
    finally { setSaving(false); }
  };

  const saveInfo = async () => {
    setSaving(true);
    try {
      await api.put('/workshops/info', { title: info.title, description: info.description });
      setMsg('Info saved');
    } catch (err) { setMsg('Error saving info'); }
    finally { setSaving(false); }
  };

  const handleHeaderImage = async (file) => {
    if (!file) return;
    setProgress(prev => ({ ...prev, header: 0 }));
    
    const timer = setInterval(() => {
        setProgress(prev => {
            if(prev.header < 90) return { ...prev, header: prev.header + 10 };
            return prev;
        });
    }, 100);

    try {
        const fd = new FormData(); fd.append('image', file);
        const res = await api.upload('/workshops/header/image', fd);
        
        clearInterval(timer);
        setProgress(prev => ({ ...prev, header: 100 }));
        
        setHeader(res);
        setTimeout(() => setProgress(prev => ({ ...prev, header: 0 })), 500);
    } catch (err) {
        clearInterval(timer);
        setProgress(prev => ({ ...prev, header: 0 }));
        setMsg(err.message || 'Error uploading header image. Max size is 5MB.');
    }
  };

  const handleInfoImage = async (file) => {
    if (!file) return;
    setProgress(prev => ({ ...prev, info: 0 }));
    
    const timer = setInterval(() => {
        setProgress(prev => {
            if(prev.info < 90) return { ...prev, info: prev.info + 10 };
            return prev;
        });
    }, 100);

    try {
        const fd = new FormData(); fd.append('image', file);
        const res = await api.upload('/workshops/info/image', fd);
        
        clearInterval(timer);
        setProgress(prev => ({ ...prev, info: 100 }));
        
        setInfo(res);
        setTimeout(() => setProgress(prev => ({ ...prev, info: 0 })), 500);
    } catch (err) {
        clearInterval(timer);
        setProgress(prev => ({ ...prev, info: 0 }));
        setMsg(err.message || 'Error uploading info image. Max size is 5MB.');
    }
  };

  const [postMsg, setPostMsg] = useState('');

  const handlePostSave = async () => {
    if (!currentPost.title.trim()) { setPostMsg('Title is required.'); return; }
    setSaving(true);
    setPostMsg('');
    try {
      if (currentPost._id) {
        await api.put(`/workshops/posts/${currentPost._id}`, { title: currentPost.title, description: currentPost.description });
      } else {
        await api.post('/workshops/posts', { title: currentPost.title, description: currentPost.description });
      }
      setView('list');
      loadData();
    } catch (err) { setPostMsg(err.message || 'Error saving post.'); }
    finally { setSaving(false); }
  };

  const handlePostCreate = async () => {
    if (!currentPost.title.trim()) { setPostMsg('Title is required.'); return; }
    setSaving(true);
    setPostMsg('');
    try {
      const created = await api.post('/workshops/posts', { title: currentPost.title, description: currentPost.description });
      setCurrentPost(created);
      setPostMsg('Post created. You can now add images.');
    } catch (err) { setPostMsg(err.message || 'Error creating post.'); }
    finally { setSaving(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await api.delete(`/workshops/posts/${id}`);
      loadData();
    } catch (err) { setMsg(err.message || 'Error deleting post.'); }
  };

  const handlePostImage = async (files) => {
    if (!files) return;
    const fileList = Array.isArray(files) ? files : [files];
    let updatedPost = { ...currentPost };
    setPostMsg('');

    for (const file of fileList) {
        setProgress(prev => ({ ...prev, post: 0 }));
        const timer = setInterval(() => {
            setProgress(prev => {
                if(prev.post < 90) return { ...prev, post: prev.post + 10 };
                return prev;
            });
        }, 100);

        const fd = new FormData(); 
        fd.append('image', file);
        try {
            const res = await api.upload(`/workshops/posts/${updatedPost._id}/image`, fd);
            updatedPost = res;
            clearInterval(timer);
            setProgress(prev => ({ ...prev, post: 100 }));
        } catch (err) {
            clearInterval(timer);
            setProgress(prev => ({ ...prev, post: 0 }));
            setPostMsg(err.message || 'Error uploading image. Max size is 5MB.');
        }
        await new Promise(r => setTimeout(r, 200));
    }
    setTimeout(() => setProgress(prev => ({ ...prev, post: 0 })), 500);
    setCurrentPost(updatedPost);
  };

  const deletePostImage = async (idx) => {
    try {
      const res = await api.delete(`/workshops/posts/${currentPost._id}/image/${idx}`);
      setCurrentPost(res);
    } catch (err) { setPostMsg(err.message || 'Error deleting image.'); }
  };

  if (view === 'post') {
    const isNew = !currentPost._id;
    return (
      <Container>
        <h2>{isNew ? 'New Post' : 'Edit Post'}</h2>
        {postMsg && <div style={{color: postMsg.startsWith('Post created') ? 'green' : 'red', marginBottom:'1rem'}}>{postMsg}</div>}
        <Card>
          <CardContent>
            <FormGroup><label>Title</label><Input value={currentPost.title} onChange={e=>{setCurrentPost({...currentPost, title:e.target.value}); setPostMsg('');}} /></FormGroup>
            <FormGroup>
              <label>Description</label>
              <DescriptionEditor
                blocks={parseDescription(currentPost.description)}
                onChange={blocks => { setCurrentPost({...currentPost, description: serializeDescription(blocks)}); setPostMsg(''); }}
              />
            </FormGroup>
            {isNew ? (
              <Button onClick={handlePostCreate} disabled={saving}>{saving ? 'Creating...' : 'Create Post & Add Images'}</Button>
            ) : (
              <>
                <FormGroup>
                  <label>Images</label>
                  <ImageUploader onImageSelect={handlePostImage} multiple={true} />
                  {progress.post > 0 && <ProgressBar progress={progress.post} />}
                  <div style={{display:'flex', gap:'1rem', marginTop:'1rem', flexWrap:'wrap'}}>
                    {currentPost.images?.map((img, i) => (
                      <div key={i} style={{position:'relative', width:'80px'}}>
                        <img src={getImageUrl(img)} alt="" style={{width:'100%', height:'80px', objectFit:'cover', borderRadius:'4px'}} />
                        <button onClick={()=>deletePostImage(i)} style={{position:'absolute', top:0, right:0, background:'red', color:'white', border:'none', cursor:'pointer', width:'20px', height:'20px', borderRadius:'50%', fontSize:'12px'}}>x</button>
                      </div>
                    ))}
                  </div>
                </FormGroup>
                <Button onClick={handlePostSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
              </>
            )}
            <Button onClick={()=>{setView('list'); setPostMsg('');}} $variant="secondary" style={{marginLeft:'1rem'}}>Cancel</Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h2>Manage Workshops</h2>
      {msg && <div style={{color: msg.toLowerCase().startsWith('error') || msg.toLowerCase().includes('too large') ? 'red' : 'green', marginBottom:'1rem'}}>{msg}</div>}

      <Card style={{marginBottom:'2rem'}}>
        <CardContent>
          <h3>Header</h3>
          <FormGroup><label>Title</label><Input value={header.title} onChange={e=>setHeader({...header, title:e.target.value})} /></FormGroup>
          <FormGroup><label>Description</label><TextArea value={header.description} onChange={e=>setHeader({...header, description:e.target.value})} /></FormGroup>
          <ImageUploader previewUrl={header.image} onImageSelect={handleHeaderImage} />
          {progress.header > 0 && <ProgressBar progress={progress.header} />}
          <Button onClick={saveHeader} style={{marginTop:'1rem'}} disabled={saving}>{saving?'Saving...':'Save Header'}</Button>
        </CardContent>
      </Card>

      <Card style={{marginBottom:'2rem'}}>
        <CardContent>
          <h3>Info Section</h3>
          <FormGroup><label>Title</label><Input value={info.title} onChange={e=>setInfo({...info, title:e.target.value})} /></FormGroup>
          <FormGroup><label>Description</label><TextArea value={info.description} onChange={e=>setInfo({...info, description:e.target.value})} /></FormGroup>
          <ImageUploader previewUrl={info.image} onImageSelect={handleInfoImage} />
          {progress.info > 0 && <ProgressBar progress={progress.info} />}
          <Button onClick={saveInfo} style={{marginTop:'1rem'}} disabled={saving}>{saving?'Saving...':'Save Info'}</Button>
        </CardContent>
      </Card>

      <HeaderRow><h3>Posts</h3><Button onClick={()=>{setCurrentPost({title:'', description:''}); setView('post')}}>Add Post</Button></HeaderRow>
      <Card>
        {posts.map(post => (
          <div key={post._id} style={{padding:'1rem', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between'}}>
            <span>{post.title}</span>
            <div>
              <Button onClick={()=>{setCurrentPost(post); setView('post')}} $variant="secondary" style={{marginRight:'0.5rem'}}>Edit</Button>
              <Button onClick={()=>deletePost(post._id)} $variant="danger">Delete</Button>
            </div>
          </div>
        ))}
      </Card>
    </Container>
  );
};

export default WorkshopsAdmin;
